import React from 'react';
import { PlusIcon, Trash2Icon } from 'lucide-react';
import { Button } from '../ui/Button';
import type { WebsiteAward, WebsiteContent, WebsiteEndorsement, WebsiteMember, WebsiteVenueCredit } from '../../data/website';
interface AboutEditorCardProps {
  draft: WebsiteContent;
  update: <K extends keyof WebsiteContent>(key: K, value: WebsiteContent[K]) => void;
  Field: React.ComponentType<{
    label: string;
    value: string;
    onChange: (value: string) => void;
    helper?: string;
  }>;
  TextField: React.ComponentType<{
    label: string;
    value: string;
    onChange: (value: string) => void;
  }>;
  ImageField: React.ComponentType<{
    label: string;
    value: string;
    onChange: (value: string) => void;
    alt: string;
  }>;
}
export function AboutEditorCard({
  draft,
  update,
  Field,
  TextField,
  ImageField
}: AboutEditorCardProps) {
  const setIn = <T extends {
    id: string;
  },>(list: T[], id: string, key: keyof T, value: string) => list.map(entry => entry.id === id ? {
    ...entry,
    [key]: value
  } : entry);
  return <div className="space-y-4" data-id="element-676" data-mp="9jnznzo3deph">
      <p className="rounded-lg border border-white/[0.07] bg-ink-800 p-2.5 text-[11px] leading-relaxed text-zinc-500" data-id="element-677" data-mp="9je8w9v833iq">
        This page lives at <span className="text-zinc-300" data-id="element-678" data-mp="3vs5q8qkrmzaq">/about</span> and is linked from the homepage and site
        navigation. Everything here is published as readable text, which is what search engines index.
      </p>

      <Group title="Page opening" data-id="element-679" data-mp="sanj6lcthwbd">
        <div className="grid gap-2 sm:grid-cols-2" data-id="element-680" data-mp="sbp6bo73y99g">
          <Field label="Eyebrow" value={draft.aboutEyebrow} onChange={value => update('aboutEyebrow', value)} data-id="element-681" data-mp="xa0eluhr8bpk" />
          <Field label="Page heading (H1)" value={draft.aboutTitle} onChange={value => update('aboutTitle', value)} helper="One heading only — keep the band name in it." data-id="element-682" data-mp="ilx6aeg8onxl" />
        </div>
        <ImageField label="Opening photo" value={draft.aboutHeroImageUrl} onChange={value => update('aboutHeroImageUrl', value)} alt="About page opening photo" data-id="element-683" data-mp="1b8qefhvykphm" />
        <p className="text-[11px] leading-relaxed text-zinc-500" data-id="element-684" data-mp="1punnenbgzv4l">
          Leave blank to use your homepage photo. A tall or square shot works best here — it sits beside the headline.
        </p>
        <Field label="Banner photo description" value={draft.aboutHeroImageAlt} onChange={value => update('aboutHeroImageAlt', value)} helper="Describe what is actually in the photo — this is read aloud by screen readers and indexed by Google." data-id="element-685" data-mp="1gy6cnl70pz9d" />
      </Group>

      <Group title="Story" data-id="element-686" data-mp="125colbdokat3">
        <TextField label="Biography" value={draft.aboutBody} onChange={value => update('aboutBody', value)} data-id="element-687" data-mp="x7krm2y6hyi1" />
        <p className="text-[11px] leading-relaxed text-zinc-500" data-id="element-688" data-mp="t7irhy83dd4v">
          Leave a blank line between paragraphs. Name your home city, where the band started, your genre, and the
          artists and venues that build credibility.
        </p>
        <ImageField label="Photo beside the story" value={draft.aboutImageUrl} onChange={value => update('aboutImageUrl', value)} alt="Band photo" data-id="element-689" data-mp="jaecqjgksjy3" />
        <Field label="Photo description" value={draft.aboutImageAlt} onChange={value => update('aboutImageAlt', value)} data-id="element-690" data-mp="2zwuhuevzyjry" />
        <ImageField label="Full-width live photo" value={draft.aboutFeatureImageUrl} onChange={value => update('aboutFeatureImageUrl', value)} alt="Live performance photo" data-id="element-691" data-mp="38v66wch7t9ia" />
        <Field label="Live photo description" value={draft.aboutFeatureImageAlt} onChange={value => update('aboutFeatureImageAlt', value)} data-id="element-692" data-mp="2h3gxf3pwtvql" />
      </Group>

      <Group title="Band members" data-id="element-693" data-mp="2pwtvek2tznjj">
        {draft.aboutMembers.map(member => <Row key={member.id} onRemove={() => update('aboutMembers', draft.aboutMembers.filter(entry => entry.id !== member.id))} label={member.name || 'member'} data-id="element-694" data-mp="1texpiparvkz1">
            <Field label="Name" value={member.name} onChange={value => update('aboutMembers', setIn<WebsiteMember>(draft.aboutMembers, member.id, 'name', value))} data-id="element-695" data-mp="1eauvnk9n4jld" />
            <Field label="Role" value={member.role} onChange={value => update('aboutMembers', setIn<WebsiteMember>(draft.aboutMembers, member.id, 'role', value))} data-id="element-696" data-mp="1o28zg48mk5kb" />
          </Row>)}
        <AddButton label="Add member" onClick={() => update('aboutMembers', [...draft.aboutMembers, {
        id: `member-${Date.now()}`,
        name: '',
        role: ''
      }])} data-id="element-697" data-mp="1s32pb35mnufu" />
      </Group>

      <Group title="Live performances" data-id="element-698" data-mp="2218pngbscsil">
        <TextField label="Section intro" value={draft.aboutVenuesIntro} onChange={value => update('aboutVenuesIntro', value)} data-id="element-699" data-mp="222a0pkssxf0b" />
        {draft.aboutVenues.map(venue => <Row key={venue.id} onRemove={() => update('aboutVenues', draft.aboutVenues.filter(entry => entry.id !== venue.id))} label={venue.venue || 'venue'} data-id="element-700" data-mp="7oiruniaqz5x">
            <Field label="Venue" value={venue.venue} onChange={value => update('aboutVenues', setIn<WebsiteVenueCredit>(draft.aboutVenues, venue.id, 'venue', value))} data-id="element-701" data-mp="2o949oxyz4usj" />
            <Field label="Detail (optional)" value={venue.detail} onChange={value => update('aboutVenues', setIn<WebsiteVenueCredit>(draft.aboutVenues, venue.id, 'detail', value))} data-id="element-702" data-mp="2ehhukqjimbki" />
            <Field label="City" value={venue.city} onChange={value => update('aboutVenues', setIn<WebsiteVenueCredit>(draft.aboutVenues, venue.id, 'city', value))} data-id="element-703" data-mp="1qmoww9hhslbb" />
          </Row>)}
        <AddButton label="Add venue" onClick={() => update('aboutVenues', [...draft.aboutVenues, {
        id: `venue-${Date.now()}`,
        venue: '',
        detail: '',
        city: ''
      }])} data-id="element-704" data-mp="3kxttwuy2pzho" />
      </Group>

      <Group title="Shared the stage" data-id="element-705" data-mp="3jj0wtt42udc2">
        <TextField label="Copy" value={draft.aboutSharedStage} onChange={value => update('aboutSharedStage', value)} data-id="element-706" data-mp="3jhy89ru88mzb" />
        <p className="text-[11px] leading-relaxed text-zinc-500" data-id="element-707" data-mp="39ktjdk8zydet">
          Only use artist photos or logos if you have approved assets. Names as text carry the same search value.
        </p>
      </Group>

      <Group title="Awards & recognition" data-id="element-708" data-mp="63bb5bpbb4dv">
        {draft.aboutAwards.map(award => <Row key={award.id} onRemove={() => update('aboutAwards', draft.aboutAwards.filter(entry => entry.id !== award.id))} label={award.title || 'award'} data-id="element-709" data-mp="1cuwxpdo3eqg">
            <Field label="Label" value={award.label} onChange={value => update('aboutAwards', setIn<WebsiteAward>(draft.aboutAwards, award.id, 'label', value))} data-id="element-710" data-mp="ttyxv33vzykh" />
            <Field label="Award" value={award.title} onChange={value => update('aboutAwards', setIn<WebsiteAward>(draft.aboutAwards, award.id, 'title', value))} data-id="element-711" data-mp="xcejq42v4kh6" />
            <Field label="Detail (optional)" value={award.detail} onChange={value => update('aboutAwards', setIn<WebsiteAward>(draft.aboutAwards, award.id, 'detail', value))} data-id="element-712" data-mp="16agfa43tukx2" />
            <ImageField label="Presenter logo" value={award.logoUrl} onChange={value => update('aboutAwards', setIn<WebsiteAward>(draft.aboutAwards, award.id, 'logoUrl', value))} alt={`${award.title || 'Award'} logo`} data-id="element-713" data-mp="1g8ebpevubo0r" />
            <Field label="Logo link (optional)" value={award.logoLinkUrl} onChange={value => update('aboutAwards', setIn<WebsiteAward>(draft.aboutAwards, award.id, 'logoLinkUrl', value))} helper="Where the logo goes when clicked, e.g. the station's website." data-id="element-714" data-mp="1u3q4vhucl6fk" />
          </Row>)}
        <AddButton label="Add award" onClick={() => update('aboutAwards', [...draft.aboutAwards, {
        id: `award-${Date.now()}`,
        label: 'Winner',
        title: '',
        detail: '',
        logoUrl: '',
        logoLinkUrl: ''
      }])} data-id="element-715" data-mp="2kbdtedf704pw" />
      </Group>

      <Group title="What other artists are saying" data-id="element-716" data-mp="25kogn6m32hjx">
        {draft.aboutEndorsements.map(entry => <Row key={entry.id} onRemove={() => update('aboutEndorsements', draft.aboutEndorsements.filter(item => item.id !== entry.id))} label={entry.artist || 'quote'} data-id="element-717" data-mp="21jckei8pey2r">
            <Field label="Artist" value={entry.artist} onChange={value => update('aboutEndorsements', setIn<WebsiteEndorsement>(draft.aboutEndorsements, entry.id, 'artist', value))} data-id="element-718" data-mp="1mvzwimvfcg97" />
            <TextField label="Quote" value={entry.quote} onChange={value => update('aboutEndorsements', setIn<WebsiteEndorsement>(draft.aboutEndorsements, entry.id, 'quote', value))} data-id="element-719" data-mp="3tiffyppi7nvp" />
            <ImageField label="Artist photo" value={entry.imageUrl} onChange={value => update('aboutEndorsements', setIn<WebsiteEndorsement>(draft.aboutEndorsements, entry.id, 'imageUrl', value))} alt={entry.artist || 'Artist photo'} data-id="element-720" data-mp="3pjq10sothy9y" />
          </Row>)}
        <p className="text-[11px] leading-relaxed text-zinc-500" data-id="element-721" data-mp="c3ipbwdzh7ca">
          Only use artist photos you have permission to publish. Leaving one blank still shows the quote.
        </p>
        <AddButton label="Add quote" onClick={() => update('aboutEndorsements', [...draft.aboutEndorsements, {
        id: `quote-${Date.now()}`,
        artist: '',
        quote: '',
        imageUrl: ''
      }])} data-id="element-722" data-mp="2wu9g0g3j1jnr" />
      </Group>

      <Group title="Closing call to action" data-id="element-723" data-mp="13n60dpdkd2au">
        <Field label="Headline" value={draft.aboutCtaHeadline} onChange={value => update('aboutCtaHeadline', value)} data-id="element-724" data-mp="ovfcke3fusn5" />
        <TextField label="Supporting copy" value={draft.aboutCtaBody} onChange={value => update('aboutCtaBody', value)} data-id="element-725" data-mp="ttz02ixokchm" />
        <p className="text-[11px] leading-relaxed text-zinc-500" data-id="element-726" data-mp="fy504pevwxgn">
          The three buttons link to your music, shows, and booking contact automatically.
        </p>
      </Group>

      <Group title="Search & sharing for this page" data-id="element-727" data-mp="5zfgos7cb4ya">
        <Field label="Page title" value={draft.aboutSeoTitle} onChange={value => update('aboutSeoTitle', value)} helper={`${draft.aboutSeoTitle.length}/60 characters`} data-id="element-728" data-mp="aw5ybjysm8lt" />
        <TextField label="Search description" value={draft.aboutSeoDescription} onChange={value => update('aboutSeoDescription', value)} data-id="element-729" data-mp="259niqy3wipcm" />
        <Field label="Share title" value={draft.aboutShareTitle} onChange={value => update('aboutShareTitle', value)} data-id="element-730" data-mp="3ku6b600hmzbp" />
        <TextField label="Share description" value={draft.aboutShareDescription} onChange={value => update('aboutShareDescription', value)} data-id="element-731" data-mp="3dyaip4xiesq" />
        <ImageField label="Share image" value={draft.aboutShareImageUrl} onChange={value => update('aboutShareImageUrl', value)} alt="About page share image" data-id="element-732" data-mp="cd1sfpsd0o5n" />
        <p className="text-[11px] leading-relaxed text-zinc-500" data-id="element-733" data-mp="mazhgfi1mozb">
          A wide performance photo around 1200 × 630 works best. Leave blank to reuse the banner photo.
        </p>
      </Group>
    </div>;
}
function Group({
  title,
  children
}: {
  title: string;
  children: React.ReactNode;
}) {
  return <section className="rounded-xl border border-white/[0.08] bg-ink-800 p-3" data-id="element-734" data-mp="2j5m3k9gtnn69">
      <h4 className="mb-2.5 text-[12px] font-medium text-zinc-300" data-id="element-735" data-mp="38t82puoykn84">{title}</h4>
      <div className="space-y-2.5" data-id="element-736" data-mp="3ctrwqpe8jaq1">{children}</div>
    </section>;
}
function Row({
  children,
  onRemove,
  label
}: {
  children: React.ReactNode;
  onRemove: () => void;
  label: string;
}) {
  return <div className="flex items-start gap-2 rounded-lg border border-white/[0.06] bg-ink-850 p-2.5" data-id="element-737" data-mp="3mrxztabs2mwh">
      <div className="grid min-w-0 flex-1 gap-2 sm:grid-cols-2" data-id="element-738" data-mp="3rrk9xawb7n12">{children}</div>
      <button type="button" aria-label={`Remove ${label}`} onClick={onRemove} className="mt-6 rounded-lg p-1.5 text-zinc-600 transition-colors hover:bg-white/[0.06] hover:text-rose-300" data-id="element-739" data-mp="3rphluwepw3yz">
        <Trash2Icon className="h-3.5 w-3.5" data-id="element-740" data-mp="9duk89slpqoj" />
      </button>
    </div>;
}
function AddButton({
  label,
  onClick
}: {
  label: string;
  onClick: () => void;
}) {
  return <Button variant="secondary" size="sm" type="button" onClick={onClick} data-id="element-741" data-mp="eal292nyuwcl">
      <PlusIcon className="h-3.5 w-3.5" data-id="element-742" data-mp="jbqvd1ljglmn" /> {label}
    </Button>;
}